// frontend/src/pages/PoliticaChat.jsx
import { useState } from "react";
import { ArrowLeft, Download } from "lucide-react";

export default function PoliticaChat() {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const pdfUrl = `${process.env.PUBLIC_URL}/pdfs/politica-chat.pdf`;

  const handleDownloadPDF = () => {
    setIsGeneratingPDF(true);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'politica-chat.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsGeneratingPDF(false);
  };

  return (
    <>
      <style>
        {`
          @media print {
            body { margin: 0; padding: 0; background: white; }
            .no-print { display: none !important; }
            .print-content { margin: 0; padding: 0.5cm; width: 100%; }
            .print-content h1 { font-size: 18pt; margin-bottom: 10pt; }
            .print-content h2 { font-size: 14pt; margin-top: 15pt; margin-bottom: 8pt; page-break-after: avoid; }
            .print-content p { font-size: 10pt; line-height: 1.4; margin-bottom: 6pt; page-break-inside: avoid; }
            .print-content ul { margin: 0 0 8pt 0; padding-left: 20pt; }
            .print-content li { font-size: 10pt; margin-bottom: 3pt; page-break-inside: avoid; }
            .print-content .section { page-break-inside: avoid; margin-bottom: 12pt; }
            .print-content .border-b { border-bottom: 0.5pt solid #ccc; }
            @page { size: A4; margin: 1.5cm; }
          }
        `}
      </style>

      <div className="min-h-screen bg-gray-50 pt-2 pb-12">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="no-print bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img
                  src={`${process.env.PUBLIC_URL}/img/logo2.png`}
                  alt="Brechó Vizinho"
                  className="h-12 w-auto object-contain"
                />
                <div>
                  <h1 className="text-xl font-bold text-gray-800">Política de Chat e Mensagens</h1>
                  <p className="text-xs text-gray-500">Versão 1.0 | 15/06/2026</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => window.history.back()}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Voltar</span>
                </button>
                <button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-[#449879] text-white rounded-lg hover:bg-[#2d5f4d] transition-colors disabled:opacity-50"
                >
                  {isGeneratingPDF ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Baixar PDF</span>
                </button>
              </div>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 print:shadow-none print:p-0 print:bg-white">
            <div className="print-content prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-strong:text-gray-800">
              
              {/* TÍTULO */}
              <div className="text-center mb-8 print:mb-4">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">POLÍTICA DE CHAT E MENSAGENS</h1>
                <p className="text-sm text-gray-500">Versão 1.0 | Data de Vigência: 15/06/2026</p>
                <div className="w-20 h-1 bg-[#449879] mx-auto mt-3 rounded-full print:bg-gray-400" />
              </div>

              {/* INTRODUÇÃO */}
              <p className="text-gray-600 leading-relaxed mb-4">
                O Chat do Brechó Vizinho é uma ferramenta disponibilizada para facilitar a comunicação entre usuários da
                plataforma.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Esta Política estabelece as regras aplicáveis à utilização do Chat e das mensagens trocadas entre os usuários.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Ao utilizar o Chat, o usuário declara que leu, compreendeu e concorda com as disposições desta Política.
              </p>

              {/* 1. FINALIDADE DO CHAT */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">1. FINALIDADE DO CHAT</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Chat do Brechó Vizinho existe exclusivamente para facilitar a comunicação entre usuários interessados em
                  anúncios, doações, produtos, serviços, oportunidades e demais conteúdos disponibilizados na plataforma.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Chat tem como objetivo:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Permitir esclarecimentos sobre anúncios;</li>
                  <li>Facilitar a comunicação entre moradores;</li>
                  <li>Auxiliar negociações entre usuários;</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O Chat não deve ser utilizado para finalidades incompatíveis com os objetivos da plataforma.
                </p>
              </div>

              {/* 2. RESPONSABILIDADE PELO CONTEÚDO DAS MENSAGENS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">2. RESPONSABILIDADE PELO CONTEÚDO DAS MENSAGENS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Todo conteúdo enviado através do Chat é de exclusiva responsabilidade dos usuários envolvidos na
                  comunicação.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho não cria, altera, aprova ou valida previamente o conteúdo das mensagens trocadas entre
                  usuários.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os usuários são integralmente responsáveis por:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Mensagens enviadas;</li>
                  <li>Arquivos compartilhados;</li>
                  <li>Informações fornecidas;</li>
                  <li>Promessas realizadas;</li>
                  <li>Negociações conduzidas através do Chat.</li>
                </ul>
              </div>

              {/* 3. CONTEÚDO PERMITIDO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">3. CONTEÚDO PERMITIDO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Chat deverá ser utilizado de forma respeitosa, educada e compatível com os objetivos da comunidade.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  São permitidas mensagens relacionadas a:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Produtos anunciados;</li>
                  <li>Doações;</li>
                  <li>Serviços;</li>
                  <li>Negociações entre usuários;</li>
                  <li>Esclarecimentos sobre anúncios.</li>
                </ul>
              </div>

              {/* 4. CONTEÚDOS PROIBIDOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">4. CONTEÚDOS PROIBIDOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  É proibido utilizar o Chat para transmitir:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Conteúdo ofensivo;</li>
                  <li>Ameaças;</li>
                  <li>Assédio;</li>
                  <li>Discriminação;</li>
                  <li>Discurso de ódio;</li>
                  <li>Conteúdo pornográfico;</li>
                  <li>Conteúdo violento;</li>
                  <li>Informações falsas;</li>
                  <li>Fraudes;</li>
                  <li>Golpes;</li>
                  <li>Spam;</li>
                  <li>Correntes;</li>
                  <li>Propaganda enganosa;</li>
                  <li>Arquivos maliciosos;</li>
                  <li>Vírus ou códigos nocivos;</li>
                  <li>Conteúdo ilícito ou contrário à legislação vigente.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Também é proibido utilizar o Chat para promover atividades ilegais ou produtos proibidos pelas políticas da
                  plataforma.
                </p>
              </div>

              {/* 5. RESPEITO ENTRE MORADORES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">5. RESPEITO ENTRE MORADORES</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho é uma comunidade baseada em confiança e boa convivência.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os usuários comprometem-se a manter comunicação respeitosa e cordial.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Não serão toleradas condutas abusivas, intimidatórias ou offensivas contra outros usuários.
                </p>
              </div>

              {/* 6. ENVIO DE DADOS PESSOAIS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">6. ENVIO DE DADOS PESSOAIS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os usuários devem agir com cautela ao compartilhar informações pessoais através do Chat.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho recomenda que informações sensíveis sejam compartilhadas apenas quando estritamente
                  necessário para a conclusão de negociações legítimas.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Cada usuário é responsável pelas informações que decidir compartilhar voluntariamente.
                </p>
              </div>

              {/* 7. AUSÊNCIA DE GARANTIAS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">7. AUSÊNCIA DE GARANTIAS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho não garante:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Veracidade das mensagens;</li>
                  <li>Identidade dos usuários;</li>
                  <li>Cumprimento de acordos;</li>
                  <li>Conclusão de negociações;</li>
                  <li>Segurança absoluta das comunicações.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O uso do Chat ocorre por conta e risco dos próprios usuários.
                </p>
              </div>

              {/* 8. MONITORAMENTO E MODERAÇÃO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">8. MONITORAMENTO E MODERAÇÃO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Para proteger a comunidade, o Brechó Vizinho poderá utilizar mecanismos automatizados para identificar:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Spam;</li>
                  <li>Fraudes;</li>
                  <li>Conteúdo ofensivo;</li>
                  <li>Conteúdo ilícito;</li>
                  <li>Comportamentos suspeitos;</li>
                  <li>Orientações das políticas da plataforma.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O uso dessas ferramentas não implica monitoramento constante ou garantia de detecção de todas as infrações.
                </p>
              </div>

              {/* 9. ACESSO ÀS MENSAGENS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">9. ACESSO ÀS MENSAGENS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Chat não constitui serviço de comunicação privada com criptografia de ponta a ponta.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O acesso humano ao conteúdo das mensagens será restrito às situações estritamente necessárias para:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Investigação de denúncias;</li>
                  <li>Prevenção de fraudes;</li>
                  <li>Garantia da segurança da plataforma;</li>
                  <li>Cumprimento de obrigações legais;</li>
                  <li>Atendimento de solicitações das autoridades competentes;</li>
                  <li>Proteção dos direitos do Brechó Vizinho, dos usuários ou de terceiros.</li>
                </ul>
              </div>

              {/* 10. DENÚNCIAS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">10. DENÚNCIAS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os usuários poderão denunciar mensagens, comportamentos ou conteúdos que considerem incompatíveis
                  com esta Política.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  As denúncias serão analisadas pela equipe responsável.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  O Brechó Vizinho poderá solicitar informações adicionais para apuração dos fatos.
                </p>
              </div>

              {/* 11. MEDIDAS APLICÁVEIS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">11. MEDIDAS APLICÁVEIS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Em caso de descumprimento desta Política, o Brechó Vizinho poderá aplicar uma ou mais das seguintes
                  medidas:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Advertência;</li>
                  <li>Restrição temporária do Chat;</li>
                  <li>Suspensão de funcionalidades;</li>
                  <li>Suspensão da conta;</li>
                  <li>Bloqueio da conta;</li>
                  <li>Exclusão definitiva da conta.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  As medidas poderão ser adotadas sem aviso prévio quando necessário para proteção da comunidade.
                </p>
              </div>

              {/* 12. GUARDA DAS MENSAGENS E REGISTROS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">12. GUARDA DAS MENSAGENS E REGISTROS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  As mensagens, registros de acesso e informações relacionadas à utilização do Chat poderão ser armazenados
                  pelo período necessário para:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Funcionamento da plataforma;</li>
                  <li>Segurança da comunidade;</li>
                  <li>Prevenção de fraudes;</li>
                  <li>Cumprimento de obrigações legais;</li>
                  <li>Exercício regular de direitos;</li>
                  <li>Atendimento de determinações judiciais ou administrativas.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O tratamento dessas informações observará a Política de Privacidade e a legislação aplicável.
                </p>
              </div>

              {/* 13. COOPERAÇÃO COM AUTORIDADES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">13. COOPERAÇÃO COM AUTORIDADES</h2>
                <p className="text-gray-600 leading-relaxed">
                  Quando exigido por lei ou por determinação de autoridade competente, o Brechó Vizinho poderá fornecer
                  informações e registros relacionados ao uso do Chat, observadas as disposições legais aplicáveis.
                </p>
              </div>

              {/* 14. ALTERAÇÕES DESTA POLÍTICA */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">14. ALTERAÇÕES DESTA POLÍTICA</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Esta Política poderá ser atualizada periodicamente para refletir alterações legais, operacionais ou tecnológicas.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  A versão mais recente estará sempre disponível na plataforma.
                </p>
              </div>

              {/* 15. DISPOSIÇÕES FINAIS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">15. DISPOSIÇÕES FINAIS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A utilização do Chat implica concordância com esta Política de Chat e Mensagens.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Os usuários comprometem-se a utilizar esta ferramenta de forma responsável, respeitosa e compatível com os
                  objetivos da comunidade.
                </p>
              </div>

              {/* ASSINATURA FINAL */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Última atualização: 15/06/2026<br />
                  Versão: 1.0<br />
                  Brechó Vizinho<br />
                  E-mail: <span className="text-[#449879] font-medium">info@brechovizinho.com.br</span>
                </p>
              </div>
            </div>
          </div>

          {/* Botão voltar ao topo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="no-print fixed bottom-20 right-4 bg-[#449879] text-white p-3 rounded-full shadow-lg hover:bg-[#2d5f4d] transition-colors z-40"
            aria-label="Voltar ao topo"
          >
            ↑
          </button>
        </div>
      </div>
    </>
  );
}